import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import SearchMapAddress from "./SearchMapAddress";
import L from "leaflet";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/client";
import { Graph } from "src/generated/graph";

const QUERY_GEO_ADDRESS = gql`
  query GeocodeAddress($lat: String, $lng: String) {
    geocodeAddress(lat: $lat, lng: $lng)
  }
`;

interface MyMarker {
  enableClick: Boolean;
  lat: number;
  lng: number;
  setCoord: ({ lat, lng }: { lat: number; lng: number }) => void;
  address: string;
  setAddress: (address: string) => void;
}

const MyMarker = (props: MyMarker) => {
  const [getGeoAddress] = useLazyQuery<Graph.Query>(QUERY_GEO_ADDRESS, {
    onCompleted: (r) => props?.setAddress(r?.geocodeAddress?.toString() || ""),
  });

  const m = useMap();

  useMapEvents({
    click: (e) => {
      if (!!props?.enableClick) {
        const lat = Number(e.latlng?.lat);
        const lng = Number(e.latlng?.lng);
        if (props.lat !== lat && props.lng !== lng) {
          props.setCoord({ lat, lng });
          getGeoAddress({ variables: { lat: lat.toString(), lng: lng.toString() } });
        }
      }
    },
  });

  useEffect(() => {
    m.flyTo({ lat: props.lat, lng: props.lng }, m.getZoom());
  }, [m, props]);

  return <Marker position={{ lat: props.lat, lng: props.lng }}></Marker>;
};

export default function PackageLocationPicker({
  lat,
  lng,
  addressName,
  onChange,
  defaultShow,
  height,
}: {
  lat?: number;
  lng?: number;
  addressName?: string;
  onChange: (lat: number, lng: number, addressName: string) => void;
  defaultShow?: boolean;
  height?: any;
}) {
  const [show, setShow] = useState(false);
  const [enableClick, setEnableClick] = useState<boolean>(false);
  const [address, setAddress] = useState(addressName);
  const [coord, setCoord] = useState<{ lat: number; lng: number }>({
    lat: lat || 11.5762787,
    lng: lng || 104.9226139,
  });

  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number }>({
    lat: 11.5762787,
    lng: 104.9226139,
  });

  const redIcon = new L.Icon({
    iconUrl: "/assets/red_pin.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [26, 46],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const onChangeAddress = (p: { lat: number; lng: number; address?: string }) => {
    const latlng = { lat: Number(p.lat), lng: Number(p.lng) };
    setCoord(latlng);
    setAddress(p.address || "");
    onChange(p.lat, p.lng, p.address + "");
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(({ coords: { latitude: lat, longitude: lng } }) => {
        const latlng = { lat, lng };
        setCurrentLocation(latlng);
      });
      if (!lat && !lng) {
        navigator.geolocation.getCurrentPosition(({ coords: { latitude: lat, longitude: lng } }) => {
          const latlng = { lat, lng };
          setCoord(latlng);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!!defaultShow) {
    return (
      <div style={{ width: "100%", marginBottom: 10 }}>
        <SearchMapAddress
          address={address || ""}
          onChangeAddress={onChangeAddress}
          enableClick={enableClick}
          setEnableClick={setEnableClick}
        />
        <MapContainer
          center={coord}
          scrollWheelZoom={true}
          style={{ width: "100%", height: height || 500 }}
          zoom={14}
          minZoom={8}
        >
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MyMarker
            {...coord}
            enableClick={enableClick}
            setCoord={(v) => {
              setCoord(v);
              setTimeout(() => {
                onChange(coord?.lat as number, coord?.lng as number, address || "");
              }, 500);
            }}
            address={address || ""}
            setAddress={(v) => {
              setAddress(v);
              setTimeout(() => {
                onChange(coord?.lat as number, coord?.lng as number, address || "");
              }, 500);
            }}
          />

          <>
            <Marker position={currentLocation} icon={redIcon} />
            <Circle center={currentLocation} radius={150} fillColor={"#ff4646"} color="#ff4646" />
          </>
        </MapContainer>
      </div>
    );
  }

  return (
    <>
      <div onClick={() => setShow(true)}>
        {addressName ? (
          <small>
            <i className="fas fa-map-marker-alt" style={{ color: "#f44336" }}></i> &nbsp;
            {addressName}
          </small>
        ) : (
          <small>
            <i>Please select location</i>
          </small>
        )}
      </div>
      <Dialog open={show} fullWidth={true} maxWidth="md" onClose={() => setShow(false)}>
        <DialogTitle>Select your customer location</DialogTitle>
        <DialogContent>
          <div style={{ width: "100%", marginBottom: 10 }}>
            <SearchMapAddress
              address={address || ""}
              onChangeAddress={onChangeAddress}
              enableClick={enableClick}
              setEnableClick={setEnableClick}
            />
            <MapContainer
              center={coord}
              scrollWheelZoom={true}
              style={{ width: "100%", height: 500 }}
              zoom={14}
              minZoom={8}
            >
              <TileLayer
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MyMarker
                {...coord}
                enableClick={enableClick}
                setCoord={setCoord}
                address={address || ""}
                setAddress={setAddress}
              />

              <>
                <Marker position={currentLocation} icon={redIcon} />
                <Circle center={currentLocation} radius={150} fillColor={"#ff4646"} color="#ff4646" />
              </>
            </MapContainer>
            <br />
            <button
              className="btn btn-primary"
              onClick={() => {
                onChange(coord?.lat as number, coord?.lng as number, address || "");
                setShow(false);
              }}
            >
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
